import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tenant, User } from '@innovation-os-ng/core/models';
import { SpaceSelectors, skipper } from '@innovation-os-ng/core/state';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subject } from 'rxjs';
import { GoogleTracker } from '../class/google.tracker.class';
import {
  AnalyticsErrorEvent,
  AnalyticsGenericEvent,
  AnalyticsPageViewEvent,
  AnalyticsTracker,
  CustomProperties,
  Tracker,
  TrackerSetup,
  TrackingIds
} from '../contracts/analytics-tracker';
import { FeatureFlagService } from './feature-flag.service';
import { TrackedPermissions } from './tracking-permission.service';
import { TenantCustomProperties, UserCustomProperties } from './tracking-setup.service';

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  // status checks
  public trackingAllowed: boolean = false;

  // Broadcase events and keep record of the latest state of different events, so we can replay them for newly initiated trackers
  public onTrackPageView: Subject<{ event: AnalyticsPageViewEvent; permissions?: TrackedPermissions }> = new ReplaySubject();
  public onTrackEvent: Subject<{ event: AnalyticsGenericEvent; permissions?: TrackedPermissions }> = new ReplaySubject();
  public onTrackError: Subject<{ event: AnalyticsErrorEvent; permissions?: TrackedPermissions }> = new ReplaySubject();
  public onSetUser: Subject<{ user: User; customProperties: UserCustomProperties }> = new ReplaySubject();
  public onSetTenant: Subject<{ tenant: Tenant; customProperties: TenantCustomProperties }> = new ReplaySubject();

  // Store references to all instantiated Trackers
  private trackers: { [id: string]: AnalyticsTracker } = {};
  // Tracking IDs can be used to map a cryptic event id against human readable tags
  private trackingIds: TrackingIds = {};

  // Store current tenant/space/user to submit with events
  private paramsCache: { tenant?: Tenant; space?: string; user?: string } = {};

  // HARD CODED set of confidential queryParams
  // TODO: We will need some sort of endpoint for fetching this from configuration!
  private confidentialQueryParams: Set<string> = new Set<string>(['sidebarFilter', 'quickFilter']);

  private permissionsCache: TrackedPermissions;

  private trackerSetup: TrackerSetup;

  // tracker initialized flag
  private initialized: boolean = false;
  private readonly destroyRef = inject(DestroyRef);
  private currentSpaceUri: string;

  constructor(
    private readonly featureFlagService: FeatureFlagService,
    private readonly store: Store
  ) {
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper(), takeUntilDestroyed(this.destroyRef))
      .subscribe((currentSpaceUri: string) => {
        this.currentSpaceUri = currentSpaceUri;
      });
  }

  public get trackingSetup() {
    return this.trackerSetup;
  }
  // set all required setup information
  public set trackingSetup(trackingSetup: TrackerSetup) {
    this.trackerSetup = trackingSetup;
    if (trackingSetup.trackingIds) {
      this.registerTrackingIds(trackingSetup.trackingIds);
    }
  }

  public set currentPermissions(perms: TrackedPermissions) {
    this.permissionsCache = perms;
  }

  /**
   * Initialise the various classes and register the tracker
   *
   * @returns void
   */
  public initTracking() {
    // go thorugh all the trackers we want to register
    if (this.trackingAllowed && !!this.trackerSetup) {
      this.trackerSetup.trackers.forEach(async (tracker) => {
        // Add each of your tracker implementations here so they can be instantiated if configured
        // unsure why we don't simply pass the instantiated objects down from the app module
        // but in the end - with a prior registration here required - this can work as well.

        if (
          (await this.featureFlagService.isFeatureEnabled('common.tracking.ga')) &&
          tracker.tracker === Tracker.GOOGLE_ANALYTICS &&
          !this.trackers[Tracker.GOOGLE_ANALYTICS]
        ) {
          const ga = new GoogleTracker(this, this.trackerSetup.user, this.trackerSetup.tenant, tracker.customProperties);
          this.registerTracker(Tracker.GOOGLE_ANALYTICS, ga);
        }
      });
      // mark as initialized
      this.initialized = true;
    }
  }

  /**
   * Register a new instance of a tracker implementation. Each registered instance gets all track events forwarded
   *
   * @param tracker Instance of Tracker
   */
  public async registerTracker(type: Tracker, tracker: AnalyticsTracker) {
    // add the tracker
    this.trackers[type] = tracker;
  }

  /**
   * Register a set of trackingIds to provide info as to what category, action, name etc should be forwarded to the analytics service
   *
   * @param trackingIds: TrackingIds KV-store with IDs and the information to be forwarded
   */
  public registerTrackingIds(trackingIds: TrackingIds) {
    Object.assign(this.trackingIds, trackingIds);
  }

  /**
   * Track Page Views
   */
  public trackPageView(tenant: Tenant, spaceId: string, url: string, userId: string, customProperties?: CustomProperties) {
    // Store arguments for future reference
    this.onTrackPageView.next({
      event: {
        tenantName: tenant.tenantSlug,
        tenantId: tenant.tenantUri,
        spaceId,
        url: this.sanitizeUrlQueryParams(url),
        userId,
        customProperties
      },
      permissions: this.permissionsCache
    });
  }

  /**
   * Track Custom User Actions
   */
  public trackEvent(
    tenant: Tenant,
    spaceId: string,
    userId: string,
    eventId: string,
    eventAction?: string,
    eventCategory?: string,
    eventLabel?: string,
    customProperties?: CustomProperties
  ) {
    // look up in the registry if there is info for this eventId
    const eventInfo = this.trackingIds[eventId];

    // Store arguments for future reference
    this.onTrackEvent.next({
      event: {
        tenantName: tenant.tenantSlug,
        tenantId: tenant.tenantUri,
        spaceId,
        userId,
        eventId,
        eventAction: (eventInfo ? eventInfo.eventAction || eventAction : eventAction) || eventId,
        eventCategory: (eventInfo ? eventInfo.eventCategory || eventCategory : eventCategory) || 'UNSET',
        eventLabel: eventInfo ? eventInfo.eventLabel || eventLabel : eventLabel,
        customProperties: eventInfo ? eventInfo.customProperties || customProperties : customProperties
      },
      permissions: this.permissionsCache
    });
  }

  /**
   * Track the given eventId, adding current tenant/space/user
   *
   * @param eventId: Event to be tracked, should be from an array
   *                 of registered tracking IDs
   * @param customProperties: Object with any other params that should be sent with the event
   */
  public async trackEventById(eventId: string, customProperties?: CustomProperties) {
    if (this.trackingAllowed && !!this.trackerSetup) {
      console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
      // Check if current space/tenant/user info is up to date
      const currentSpace = this.currentSpaceUri;
      if (this.paramsCache.space !== currentSpace) {
        this.paramsCache.tenant = this.trackerSetup.tenant;
        this.paramsCache.user = this.trackerSetup.user.sub;
        this.paramsCache.space = currentSpace;
      }

      // Track event with extra params
      this.trackEvent(
        this.paramsCache.tenant,
        this.paramsCache.space,
        this.paramsCache.user,
        eventId,
        undefined,
        undefined,
        undefined,
        customProperties
      );
    }
  }

  /**
   * Track Custom User Actions
   */
  public trackError(requestUrl: string, responseCode: string, requestId: string) {
    // Store arguments for future reference
    this.onTrackError.next({
      event: {
        requestUrl: this.sanitizeUrlQueryParams(requestUrl),
        responseCode,
        requestId,
        windowLocation: window.location.href
      },
      permissions: this.permissionsCache
    });
  }

  /** If we later want to add more queryParams to the confidential list */
  public addConfidentialQueryParams(params: Array<string> | Set<string>, replace: boolean = false) {
    this.confidentialQueryParams = new Set<string>([
      ...(replace ? [] : Array.from(this.confidentialQueryParams)),
      ...(Array.isArray(params) ? params : Array.from(params))
    ]);
  }

  public setUser(user: User, customProperties: UserCustomProperties) {
    this.onSetUser.next({ user, customProperties });
  }

  public setTenant(tenant: Tenant, customProperties: TenantCustomProperties) {
    this.onSetTenant.next({ tenant, customProperties });
  }

  /** Replace confidential info from queryParams from given url string */
  private sanitizeUrlQueryParams(url: string): string {
    const urlParts: Array<string> = url.split('?', 2);
    if (urlParts.length === 1) {
      return url;
    }
    let queryParams = urlParts.pop();
    this.confidentialQueryParams.forEach((param) => {
      // Find specific queryParam and replace its value with ...
      const searchTerm: RegExp = new RegExp(`(${param}=)([^&]*)`, 'gmi');
      queryParams = queryParams.replace(searchTerm, '$1...');
    });
    urlParts.push(queryParams);
    return urlParts.join('?');
  }
}
